import {
  defaultFont,
  grayColor,
  whiteColor,
  hexToRgb,
  cardTitle,
} from "assets/jss/material-dashboard-pro-react.js";

const ctsGuidePortalStyle = () => ({
  // =============================================================================
  // LAYOUT & CONTAINERS
  // =============================================================================

  pageContainer: {
    paddingBottom: "20px",
    marginTop: "-50px",
  },

  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },

  sectionCard: {
    marginBottom: "30px",
  },

  cardContent: {
    padding: "20px 30px",
  },

  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },

  // Header tabs positioned to the right
  headerTabsRight: {
    position: "absolute",
    right: "15px",
    top: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  headerTab: {
    ...defaultFont,
    fontSize: "0.8em",
    fontWeight: "500",
    padding: "6px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    border: "none",
    background: "transparent",
    color: grayColor[2],
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "&:hover": {
      backgroundColor: grayColor[9],
      color: grayColor[0],
    },
  },

  headerTabActive: {
    backgroundColor: grayColor[8],
    color: grayColor[0],
    fontWeight: "600",
  },

  headerTabIcon: {
    fontSize: "1em",
  },

  // =============================================================================
  // NAVIGATION - HORIZONTAL STEPPER
  // =============================================================================

  stepperContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    marginTop: "-20px",
    marginBottom: "-10px",
    paddingTop: "40px",
    paddingBottom: "-10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    gap: "0px",
    flexWrap: "nowrap",
    overflowX: "auto",
  },

  stepItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "90px",
    position: "relative",
  },

  groupLabelContainer: {
    position: "absolute",
    top: "-32px",
    left: "50%",
    transform: "translateX(-50%)",
    whiteSpace: "nowrap",
  },

  groupDivider: {
    width: "30px",
    height: "2px",
    backgroundColor: "transparent",
    marginTop: "18px",
    flexShrink: 0,
  },

  stepCircle: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: `2px solid ${grayColor[6]}`,
    backgroundColor: whiteColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    color: grayColor[3],
    fontSize: "1.1em",
    "&:hover": {
      transform: "scale(1.1)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    },
  },

  stepCircleActive: {
    color: whiteColor,
    transform: "scale(1.1)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  },

  stepCircleCompleted: {
    color: whiteColor,
  },

  stepLabel: {
    ...defaultFont,
    fontSize: "0.7em",
    color: grayColor[2],
    marginTop: "6px",
    textAlign: "center",
    cursor: "pointer",
    maxWidth: "85px",
    lineHeight: "1.2",
    transition: "all 0.3s ease",
    "&:hover": {
      color: grayColor[0],
      fontWeight: "500",
    },
  },

  stepLabelActive: {
    fontWeight: "600",
    color: grayColor[0],
  },

  stepConnector: {
    width: "25px",
    height: "2px",
    backgroundColor: grayColor[7],
    margin: "0 3px",
    marginTop: "18px",
    transition: "all 0.3s ease",
    flexShrink: 0,
  },

  // =============================================================================
  // PAGE CONTENT
  // =============================================================================

  pageContentWrapper: {
    transition: "opacity 0.25s ease",
    opacity: 1,
  },

  pageContentFading: {
    opacity: 0,
  },

  // =============================================================================
  // PROGRESS INDICATORS
  // =============================================================================

  progressContainer: {
    marginBottom: "30px",
  },

  progressBar: {
    height: "8px",
    borderRadius: "4px",
    backgroundColor: grayColor[8],
    overflow: "hidden",
    marginTop: "10px",
  },

  progressFill: {
    height: "100%",
    backgroundColor: "#4caf50",
    transition: "width 0.5s ease",
  },

  progressText: {
    ...defaultFont,
    fontSize: "0.9em",
    color: grayColor[1],
    marginBottom: "5px",
  },

  // =============================================================================
  // MODULE/CARD ITEMS
  // =============================================================================

  moduleCard: {
    padding: "16px 12px",
    marginBottom: "0",
    borderRadius: "0",
    borderBottom: `1px solid ${grayColor[9]}`,
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: grayColor[11],
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },

  moduleCardCompleted: {
    backgroundColor: `rgba(76, 175, 80, 0.03)`,
  },

  moduleCardLocked: {
    opacity: 0.6,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  moduleHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "0",
  },

  moduleIcon: {
    fontSize: "1.5em",
  },

  moduleTitle: {
    ...defaultFont,
    fontSize: "0.95em",
    fontWeight: "500",
    margin: "0",
  },

  moduleTime: {
    ...defaultFont,
    fontSize: "0.8em",
    color: grayColor[4],
  },

  // =============================================================================
  // CHECKLIST
  // =============================================================================

  checklistItem: {
    padding: "16px 12px",
    marginBottom: "0",
    borderRadius: "0",
    borderBottom: `1px solid ${grayColor[9]}`,
    backgroundColor: whiteColor,
    "&:last-child": {
      borderBottom: "none",
    },
  },

  checklistItemConfirmed: {
    backgroundColor: `rgba(76, 175, 80, 0.03)`,
  },

  checklistItemImpediment: {
    backgroundColor: `rgba(255, 152, 0, 0.03)`,
  },

  ruleText: {
    ...defaultFont,
    fontSize: "0.95em",
    fontWeight: "500",
    marginBottom: "6px",
  },

  ruleExplanation: {
    ...defaultFont,
    fontSize: "0.85em",
    color: grayColor[3],
    marginBottom: "8px",
  },

  checklistActions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },

  impedimentInput: {
    marginTop: "15px",
    width: "100%",
  },

  // =============================================================================
  // P&L TREE
  // =============================================================================

  plTreeContainer: {
    marginTop: "20px",
  },

  plTreeItem: {
    padding: "12px 15px",
    marginBottom: "5px",
    borderRadius: "4px",
    border: `1px solid ${grayColor[8]}`,
    backgroundColor: whiteColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[9],
    },
  },

  plTreeItemNested: {
    marginLeft: "30px",
  },

  plTreeItemContent: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
  },

  plTreeItemActions: {
    display: "flex",
    gap: "5px",
    opacity: 0,
    transition: "opacity 0.2s ease",
    "$plTreeItem:hover &": {
      opacity: 1,
    },
  },

  plTypeBadge: {
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "0.75em",
    fontWeight: "500",
    textTransform: "uppercase",
  },

  plTypeBadgeRevenue: {
    backgroundColor: `rgba(76, 175, 80, 0.2)`,
    color: "#2e7d32",
  },

  plTypeBadgeDeduction: {
    backgroundColor: `rgba(255, 152, 0, 0.2)`,
    color: "#e65100",
  },

  plTypeBadgeCost: {
    backgroundColor: `rgba(244, 67, 54, 0.2)`,
    color: "#c62828",
  },

  plTypeBadgeExpense: {
    backgroundColor: `rgba(156, 39, 176, 0.2)`,
    color: "#6a1b9a",
  },

  plTypeBadgeMargin: {
    backgroundColor: `rgba(33, 150, 243, 0.2)`,
    color: "#1565c0",
  },

  // =============================================================================
  // DIMENSIONS TABLE
  // =============================================================================

  dimensionsTable: {
    width: "100%",
    marginTop: "20px",
  },

  dimensionRow: {
    borderBottom: `1px solid ${grayColor[8]}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },

  dimensionCell: {
    padding: "12px 15px",
    ...defaultFont,
    fontSize: "0.9em",
  },

  dimensionTypeBadge: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "0.75em",
    fontWeight: "500",
    backgroundColor: grayColor[9],
    color: grayColor[2],
  },

  // =============================================================================
  // INPUT TRACKING TABLE - EDITABLE INLINE
  // =============================================================================

  dataTrackingContainer: {
    overflowX: "auto",
    marginTop: "20px",
  },

  inputsTable: {
    width: "100%",
    minWidth: "1200px",
    borderCollapse: "separate",
    borderSpacing: "0",
    border: `1px solid ${grayColor[7]}`,
    borderRadius: "8px",
    overflow: "hidden",
  },

  inputsTableHeader: {
    backgroundColor: grayColor[8],
  },

  inputsTableHeaderCell: {
    padding: "10px 8px",
    ...defaultFont,
    fontSize: "0.75em",
    fontWeight: "600",
    color: grayColor[1],
    borderBottom: `2px solid ${grayColor[6]}`,
    textAlign: "left",
    whiteSpace: "nowrap",
    "&:first-child": {
      paddingLeft: "12px",
    },
    "& .MuiTableSortLabel-root": {
      color: grayColor[1],
      "&:hover": {
        color: grayColor[0],
      },
      "&.Mui-active": {
        color: grayColor[0],
        fontWeight: "700",
      },
    },
    "& .MuiTableSortLabel-icon": {
      opacity: 0.5,
    },
  },

  inputsTableRow: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
    "&:nth-child(even)": {
      backgroundColor: `rgba(${hexToRgb(grayColor[9])}, 0.3)`,
    },
  },

  inputsTableCell: {
    padding: "6px 8px",
    ...defaultFont,
    fontSize: "0.85em",
    borderBottom: `1px solid ${grayColor[8]}`,
    verticalAlign: "middle",
    "&:first-child": {
      paddingLeft: "12px",
    },
  },

  // Editable cell styles
  editableCell: {
    cursor: "pointer",
    padding: "4px 6px",
    borderRadius: "4px",
    minHeight: "28px",
    display: "flex",
    alignItems: "center",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[9],
    },
  },

  editableCellCentered: {
    justifyContent: "center",
  },

  editableCellActive: {
    backgroundColor: whiteColor,
    boxShadow: `0 0 0 2px #790000`,
    padding: "0",
  },

  editableCellEmpty: {
    color: grayColor[5],
    fontStyle: "italic",
  },

  inlineInput: {
    width: "100%",
    border: "none",
    outline: "none",
    padding: "6px 8px",
    ...defaultFont,
    fontSize: "0.85em",
    backgroundColor: "transparent",
    borderRadius: "4px",
  },

  inlineSelect: {
    width: "100%",
    border: "none",
    outline: "none",
    padding: "4px 6px",
    ...defaultFont,
    fontSize: "0.85em",
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      backgroundColor: whiteColor,
    },
  },

  inlineTextarea: {
    width: "100%",
    border: "none",
    outline: "none",
    padding: "6px 8px",
    ...defaultFont,
    fontSize: "0.85em",
    backgroundColor: "transparent",
    borderRadius: "4px",
    resize: "none",
    minHeight: "60px",
    lineHeight: "1.4",
  },

  // Multiline text cell
  multilineCell: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxWidth: "200px",
    lineHeight: "1.4",
  },

  // Status badges
  statusBadge: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "0.7em",
    fontWeight: "500",
    display: "inline-block",
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      opacity: 0.8,
    },
  },

  statusNotRequested: {
    backgroundColor: `rgba(158, 158, 158, 0.15)`,
    color: "#616161",
  },

  statusRequested: {
    backgroundColor: `rgba(103, 58, 183, 0.15)`,
    color: "#512da8",
  },

  statusPartial: {
    backgroundColor: `rgba(255, 193, 7, 0.2)`,
    color: "#f57f17",
  },

  statusReceived: {
    backgroundColor: `rgba(76, 175, 80, 0.2)`,
    color: "#2e7d32",
  },

  statusDocumented: {
    backgroundColor: `rgba(33, 150, 243, 0.2)`,
    color: "#1565c0",
  },

  // Quality badges
  qualityBadge: {
    padding: "3px 8px",
    borderRadius: "10px",
    fontSize: "0.7em",
    fontWeight: "500",
    display: "inline-block",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },

  qualityHigh: {
    backgroundColor: `rgba(76, 175, 80, 0.15)`,
    color: "#2e7d32",
  },

  qualityMedium: {
    backgroundColor: `rgba(255, 152, 0, 0.15)`,
    color: "#e65100",
  },

  qualityLow: {
    backgroundColor: `rgba(244, 67, 54, 0.15)`,
    color: "#c62828",
  },

  // PL Line cell
  plLineCell: {
    fontWeight: "500",
    color: grayColor[1],
  },

  // Action buttons
  actionButtonsCell: {
    whiteSpace: "nowrap",
    width: "80px",
  },

  actionButton: {
    padding: "4px",
    marginRight: "2px",
    opacity: 0.6,
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 1,
    },
  },

  // Add row button
  addRowButton: {
    marginTop: "15px",
    marginBottom: "10px",
  },

  // =============================================================================
  // DRAWER (SPECIFICATION PANEL)
  // =============================================================================

  drawer: {
    padding: "30px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  drawerHeader: {
    marginBottom: "25px",
    paddingBottom: "20px",
    borderBottom: `2px solid ${grayColor[8]}`,
    flexShrink: 0,
  },

  drawerTitle: {
    ...defaultFont,
    fontSize: "1.3em",
    fontWeight: "500",
    margin: "0 0 10px 0",
    wordBreak: "break-word",
  },

  drawerSection: {
    marginBottom: "25px",
    flexShrink: 0,
  },

  drawerSectionFlex: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginBottom: "20px",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },

  drawerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    paddingTop: "16px",
    marginTop: "auto",
    borderTop: `1px solid ${grayColor[8]}`,
    flexShrink: 0,
  },

  // Accordion styles for drawer
  accordionRoot: {
    "& > div:first-child": {
      paddingTop: "8px",
    },
    "& .MuiAccordion-root": {
      boxShadow: "none",
      "&:before": {
        display: "none",
      },
    },
  },
  accordionTitle: {
    paddingLeft: "12px",
    color: grayColor[1],
    fontSize: "0.95em",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    "&:hover,&:focus": {
      color: grayColor[0],
    },
  },
  expandIcon: {
    color: grayColor[3],
  },

  drawerSectionTitle: {
    ...defaultFont,
    fontSize: "1em",
    fontWeight: "600",
    marginBottom: "12px",
    color: grayColor[1],
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  uploadArea: {
    border: `2px dashed ${grayColor[6]}`,
    borderRadius: "8px",
    padding: "25px 20px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "& p": {
      margin: "10px 0 0 0",
      fontSize: "0.85em",
      color: grayColor[3],
    },
    "&:hover": {
      borderColor: "#790000",
      backgroundColor: `rgba(${hexToRgb(grayColor[9])}, 0.5)`,
    },
  },

  uploadAreaActive: {
    borderColor: "#790000",
    backgroundColor: `rgba(${hexToRgb("#790000")}, 0.05)`,
  },

  // =============================================================================
  // DRAWER SPECIFICATION
  // =============================================================================

  drawerSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },

  exampleToggle: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: grayColor[3],
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#790000",
      backgroundColor: `rgba(${hexToRgb("#790000")}, 0.08)`,
    },
  },

  exampleToggleText: {
    ...defaultFont,
    fontSize: "0.8em",
    fontWeight: "500",
  },

  drawerInstructions: {
    ...defaultFont,
    fontSize: "0.85em",
    color: grayColor[3],
    marginBottom: "16px",
    lineHeight: "1.6",
    margin: 0,
    "& strong": {
      color: grayColor[1],
    },
  },

  drawerWarningBox: {
    backgroundColor: "rgba(255, 152, 0, 0.06)",
    borderRadius: "4px",
    padding: "10px 12px",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& svg": {
      color: "#ff9800",
      fontSize: "1em",
      flexShrink: 0,
      opacity: 0.7,
    },
    "& p": {
      ...defaultFont,
      fontSize: "0.78em",
      color: grayColor[2],
      margin: 0,
      lineHeight: "1.4",
    },
  },

  drawerExampleLabel: {
    ...defaultFont,
    fontSize: "0.8em",
    fontWeight: "600",
    color: grayColor[3],
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "8px",
  },

  // =============================================================================
  // DOCUMENTATION FORM
  // =============================================================================

  documentationForm: {
    marginTop: "20px",
  },

  formField: {
    marginBottom: "25px",
  },

  formLabel: {
    ...defaultFont,
    fontSize: "0.95em",
    fontWeight: "500",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    color: grayColor[1],
  },

  formTextarea: {
    width: "100%",
    minHeight: "120px",
    padding: "12px",
    borderRadius: "4px",
    border: `1px solid ${grayColor[7]}`,
    ...defaultFont,
    fontSize: "0.9em",
    resize: "vertical",
    transition: "border-color 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#790000",
    },
  },

  aiResultContainer: {
    marginTop: "30px",
    marginBottom: "30px",
  },

  aiResultRewritten: {
    padding: "20px",
    borderRadius: "8px",
    backgroundColor: `rgba(76, 175, 80, 0.1)`,
    border: `1px solid rgba(76, 175, 80, 0.3)`,
    marginBottom: "20px",
  },

  aiResultFeedback: {
    padding: "20px",
    borderRadius: "8px",
    backgroundColor: `rgba(255, 152, 0, 0.1)`,
    border: `1px solid rgba(255, 152, 0, 0.3)`,
  },

  aiResultTitle: {
    ...defaultFont,
    fontSize: "1.1em",
    fontWeight: "500",
    marginBottom: "15px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  feedbackList: {
    listStyle: "none",
    padding: "0",
    margin: "0",
  },

  feedbackItem: {
    ...defaultFont,
    fontSize: "0.9em",
    padding: "8px 0",
    paddingLeft: "20px",
    position: "relative",
    "&:before": {
      content: '"•"',
      position: "absolute",
      left: "0",
      fontWeight: "bold",
    },
  },

  // =============================================================================
  // LOCKED DOCUMENTATION
  // =============================================================================

  lockedBanner: {
    padding: "15px 20px",
    borderRadius: "8px",
    backgroundColor: `rgba(33, 150, 243, 0.1)`,
    border: `1px solid rgba(33, 150, 243, 0.3)`,
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  lockedBannerText: {
    ...defaultFont,
    fontSize: "0.95em",
    color: grayColor[1],
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  // =============================================================================
  // FEEDBACK BUTTON (FLOATING)
  // =============================================================================

  feedbackButton: {
    position: "fixed",
    bottom: "30px",
    right: "30px",
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    backgroundColor: "#790000",
    color: whiteColor,
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    zIndex: 1000,
    "&:hover": {
      backgroundColor: "#b50000",
      transform: "scale(1.1)",
    },
  },

  feedbackModal: {
    position: "fixed",
    bottom: "110px",
    right: "30px",
    width: "350px",
    backgroundColor: whiteColor,
    borderRadius: "8px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    padding: "20px",
    zIndex: 1001,
  },

  feedbackModalHeader: {
    ...defaultFont,
    fontSize: "1.1em",
    fontWeight: "500",
    marginBottom: "15px",
  },

  // =============================================================================
  // CRITERIA LIBRARY
  // =============================================================================

  criteriaLibraryLayout: {
    display: "grid",
    gridTemplateColumns: "250px 1fr",
    gap: "30px",
    marginTop: "10px",
  },

  criteriaSidebar: {
    borderRight: `1px solid ${grayColor[8]}`,
    paddingRight: "20px",
  },

  criteriaCategory: {
    padding: "10px 15px",
    marginBottom: "5px",
    borderRadius: "4px",
    cursor: "pointer",
    ...defaultFont,
    fontSize: "0.95em",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[9],
    },
  },

  criteriaCategoryActive: {
    backgroundColor: "#790000",
    color: whiteColor,
    fontWeight: "500",
  },

  criteriaContent: {
    padding: "0 20px",
  },

  criteriaCard: {
    padding: "20px",
    marginBottom: "20px",
    borderRadius: "8px",
    border: `1px solid ${grayColor[7]}`,
    backgroundColor: whiteColor,
  },

  criteriaTitle: {
    ...defaultFont,
    fontSize: "1.1em",
    fontWeight: "500",
    marginBottom: "15px",
    color: "#790000",
  },

  criteriaSection: {
    marginBottom: "15px",
  },

  criteriaSectionLabel: {
    ...defaultFont,
    fontSize: "0.85em",
    fontWeight: "600",
    textTransform: "uppercase",
    color: grayColor[3],
    marginBottom: "5px",
  },

  criteriaSectionText: {
    ...defaultFont,
    fontSize: "0.9em",
    color: grayColor[1],
  },

  // =============================================================================
  // UTILITIES
  // =============================================================================

  textMuted: {
    color: grayColor[3],
  },

  textBold: {
    fontWeight: "500",
  },

  mb10: {
    marginBottom: "10px",
  },

  mb20: {
    marginBottom: "20px",
  },

  mb30: {
    marginBottom: "30px",
  },

  mt20: {
    marginTop: "20px",
  },

  flexBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  flexCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  gap10: {
    gap: "10px",
  },

  gap20: {
    gap: "20px",
  },

  // =============================================================================
  // BUILD STATUS TRACKER
  // =============================================================================

  // Tab navigation
  tabsContainer: {
    borderBottom: `1px solid ${grayColor[8]}`,
    marginBottom: "20px",
  },

  tabButton: {
    padding: "12px 24px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "0.95em",
    fontWeight: "500",
    color: grayColor[3],
    borderBottom: "3px solid transparent",
    transition: "all 0.2s ease",
    marginRight: "8px",
    "&:hover": {
      color: grayColor[1],
    },
  },

  tabButtonActive: {
    color: grayColor[0],
    borderBottomColor: "#790000",
  },

  // Section navigation
  sectionNavContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "10px",
    padding: "4px 8px",
    backgroundColor: "transparent",
    borderRadius: "4px",
  },

  sectionNavArrow: {
    padding: "2px",
    cursor: "pointer",
    borderRadius: "50%",
    transition: "background-color 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: grayColor[3],
    "&:hover": {
      backgroundColor: grayColor[9],
      color: grayColor[0],
    },
    "&:disabled": {
      opacity: 0.3,
      cursor: "not-allowed",
    },
  },

  sectionNavArrowDisabled: {
    opacity: 0.3,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  sectionInfo: {
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  sectionDates: {
    ...defaultFont,
    fontSize: "0.8em",
    fontWeight: "500",
    color: grayColor[1],
  },

  sectionLabel: {
    ...defaultFont,
    fontSize: "0.7em",
    color: grayColor[1],
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    padding: "3px 8px",
    backgroundColor: grayColor[10],
    borderRadius: "3px",
    fontWeight: "600",
  },

  sectionActionBtn: {
    padding: "2px",
    color: grayColor[5],
    opacity: 0.6,
    transition: "all 0.15s ease",
    "&:hover": {
      opacity: 1,
      color: grayColor[1],
      backgroundColor: grayColor[9],
    },
  },

  // Build status table
  buildStatusTable: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "separate",
    borderSpacing: "0",
    border: `1px solid ${grayColor[6]}`,
    borderRadius: "6px",
    overflow: "hidden",
    backgroundColor: whiteColor,
  },

  buildStatusRow: {
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: `rgba(${hexToRgb(grayColor[9])}, 0.3)`,
    },
    "&:last-child td": {
      borderBottom: "none",
    },
  },

  buildStatusRowLevel1: {
    backgroundColor: `rgba(${hexToRgb(grayColor[8])}, 0.4)`,
    "& td": {
      fontWeight: "600",
    },
  },

  buildStatusRowLevel2: {
    backgroundColor: whiteColor,
    "& $buildStatusLineCell": {
      paddingLeft: "20px",
    },
  },

  buildStatusRowLevel3: {
    backgroundColor: whiteColor,
    "& $buildStatusLineCell": {
      paddingLeft: "36px",
    },
  },

  buildStatusHeaderCell: {
    padding: "8px 12px",
    ...defaultFont,
    fontSize: "0.8em",
    fontWeight: "700",
    color: "#333",
    backgroundColor: grayColor[8],
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    borderBottom: `1px solid ${grayColor[6]}`,
  },

  buildStatusCell: {
    padding: "5px 12px",
    ...defaultFont,
    fontSize: "0.85em",
    borderBottom: `1px solid ${grayColor[8]}`,
    verticalAlign: "middle",
    color: grayColor[0],
  },

  buildStatusLineCell: {
    fontWeight: "500",
    color: grayColor[0],
    width: "140px",
    maxWidth: "160px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  buildStatusStatusCell: {
    width: "120px",
    textAlign: "center",
  },

  buildStatusMessageCell: {
    maxWidth: "400px",
    overflow: "hidden",
  },

  // Status indicator dots
  statusDot: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "all 0.15s ease",
    border: "2px solid transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      transform: "scale(1.1)",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
    },
  },

  statusDotActive: {
    border: "2px solid #333",
    boxShadow: "0 0 0 2px #fff, 0 0 0 4px #333",
  },

  statusDotNotStarted: {
    backgroundColor: "#9e9e9e",
  },

  statusDotOnTrack: {
    backgroundColor: "#4caf50",
  },

  statusDotAtRisk: {
    backgroundColor: "#ff9800",
  },

  statusDotDelayed: {
    backgroundColor: "#f44336",
  },

  statusDotCompleted: {
    backgroundColor: "#2196f3",
  },

  // Status picker dropdown
  statusPickerContainer: {
    position: "relative",
    display: "inline-block",
  },

  statusPickerDropdown: {
    position: "absolute",
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: whiteColor,
    borderRadius: "8px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
    padding: "8px",
    zIndex: 1000,
    display: "flex",
    gap: "6px",
    marginTop: "8px",
  },

  // Key message input
  keyMessageInput: {
    width: "100%",
    border: `1px solid transparent`,
    background: "transparent",
    padding: "4px 8px",
    ...defaultFont,
    fontSize: "0.9em",
    fontWeight: "500",
    color: grayColor[0],
    borderRadius: "3px",
    transition: "all 0.15s ease",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: `rgba(${hexToRgb(grayColor[8])}, 0.4)`,
      borderColor: grayColor[7],
    },
    "&:focus": {
      backgroundColor: whiteColor,
      outline: "none",
      borderColor: grayColor[5],
      cursor: "text",
    },
    "&::placeholder": {
      color: grayColor[5],
      fontStyle: "italic",
      fontWeight: "400",
    },
  },

  keyMessageInputExpanded: {
    whiteSpace: "normal",
    overflow: "visible",
    backgroundColor: whiteColor,
    borderColor: grayColor[5],
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    cursor: "text",
  },

  keyMessageDisplay: {
    width: "100%",
    maxWidth: "100%",
    padding: "4px 8px",
    ...defaultFont,
    fontSize: "0.9em",
    fontWeight: "500",
    color: grayColor[0],
    borderRadius: "3px",
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    transition: "background-color 0.15s ease",
    minHeight: "24px",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: `rgba(${hexToRgb(grayColor[8])}, 0.4)`,
    },
  },

  keyMessageDisplayExpanded: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflow: "visible",
    backgroundColor: `rgba(${hexToRgb(grayColor[9])}, 0.3)`,
  },

  keyMessagePlaceholder: {
    color: grayColor[5],
    fontStyle: "italic",
    fontWeight: "400",
  },

  keyMessageTextarea: {
    width: "100%",
    border: `1px solid ${grayColor[5]}`,
    backgroundColor: whiteColor,
    padding: "6px 8px",
    ...defaultFont,
    fontSize: "0.9em",
    fontWeight: "500",
    color: grayColor[0],
    borderRadius: "3px",
    resize: "vertical",
    minHeight: "60px",
    outline: "none",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    "&::placeholder": {
      color: grayColor[5],
      fontStyle: "italic",
      fontWeight: "400",
    },
  },

  // Empty state
  buildStatusEmpty: {
    textAlign: "center",
    padding: "60px 20px",
    color: grayColor[3],
  },

  buildStatusEmptyIcon: {
    fontSize: "3em",
    marginBottom: "16px",
    opacity: 0.5,
  },

  buildStatusEmptyText: {
    ...defaultFont,
    fontSize: "1em",
    marginBottom: "20px",
  },

  // New section button
  newSectionButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    ...defaultFont,
    fontSize: "0.9em",
    fontWeight: "500",
    transition: "all 0.2s ease",
  },

  // Status legend
  statusLegend: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    marginTop: "16px",
    padding: "8px 12px",
    backgroundColor: "transparent",
    borderRadius: "4px",
    flexWrap: "wrap",
  },

  statusLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    ...defaultFont,
    fontSize: "0.75em",
    color: grayColor[2],
  },

  statusLegendDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
  },

  // =============================================================================
  // EXPORT DOCUMENTATION
  // =============================================================================

  exportButton: {
    marginLeft: "auto",
    opacity: 0.7,
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 1,
    },
  },

  exportDialogContent: {
    paddingTop: "16px",
  },

  exportFormatContainer: {
    marginBottom: "20px",
  },

  exportFormatLabel: {
    fontSize: "0.9em",
    fontWeight: "500",
    color: grayColor[2],
    marginBottom: "12px",
    display: "block",
  },

  exportFormatOptions: {
    display: "flex",
    gap: "16px",
    marginTop: "8px",
  },

  exportFormatOption: {
    flex: 1,
    padding: "16px",
    borderRadius: "8px",
    border: `2px solid ${grayColor[8]}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      borderColor: grayColor[5],
      backgroundColor: grayColor[10],
    },
  },

  exportFormatOptionSelected: {
    borderColor: (props) => props.clientColor || grayColor[2],
    backgroundColor: (props) =>
      `rgba(${hexToRgb(props.clientColor || grayColor[2])}, 0.08)`,
  },

  exportFormatIcon: {
    fontSize: "2em",
    color: grayColor[3],
  },

  exportFormatIconSelected: {
    color: (props) => props.clientColor || grayColor[2],
  },

  exportFormatName: {
    fontSize: "0.9em",
    fontWeight: "500",
    color: grayColor[2],
  },

  exportOptionsContainer: {
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: `1px solid ${grayColor[9]}`,
  },

  exportOptionItem: {
    marginBottom: "8px",
    "& .MuiCheckbox-root": {
      padding: "4px 8px",
    },
    "& .MuiFormControlLabel-label": {
      fontSize: "0.9em",
      color: grayColor[2],
    },
  },
});

export default ctsGuidePortalStyle;
